import { CircularProgress } from '@material-ui/core';
import { useFormik } from 'formik';
import * as React from 'react';
import { Dispatch, SetStateAction, useState } from 'react';
import { KeywordInputGq } from '../../../../../graphql/projects/mutations/projects/useRenameProjectKeywordsGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import VoxelAlert from '../../../../voxel/Alert/VoxelAlert';
import VoxelDialog from '../../../../voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../../voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActions from '../../../../voxel/Dialogs/VoxelDialogActions';
import VoxelDialogActionSubmit from '../../../../voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogContent from '../../../../voxel/Dialogs/VoxelDialogContent';
import VoxelInput from '../../../../voxel/Forms/VoxelInput';
import VoxelText from '../../../../voxel/Text/VoxelText';
import { KeywordTableState } from '../logic/useKeywordTableState';

export interface EditKeywordsDialogProps {
  tableState: KeywordTableState;
  reload?: () => Promise<any>;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const EditKeywordsDialog: React.FC<EditKeywordsDialogProps> = ({
  tableState,
  reload,
  open,
  setOpen,
}) => {
  // Data fetching
  const keywordQuery = projectsApiService.queries.useGetKeywords(
    {
      keywordQuery: {
        filtering: tableState.backendFilterState,
        selecting: tableState.backendSelectionState,
        paging: {
          page: 0,
          amountPerPage: 100,
        },
      },
    },
    {
      skip: !open,
    }
  );
  const hasTranslations = keywordQuery.keywords.some((kw) => !!kw.translatedFromKeyword?.length);

  const mutation = projectsApiService.mutations.projects.useRenameProjectKeywords();
  const [loading, setLoading] = useState(false);

  const formBag = useFormik<KeywordInputGq[]>({
    enableReinitialize: true,
    initialValues:
      keywordQuery?.keywords?.map((kw) => ({ renamedKeyword: kw.keyword, keywordId: kw.id })) ?? [],
    onSubmit: (values, actions) =>
      mutation({
        projectId: tableState.filterState.projectId,
        keywordInputs: values,
      })
        .then(reload)
        .then(tableState.handleResetSelection)
        .finally(() => {
          setOpen(false);
        }),
  });

  return (
    <VoxelDialog
      open={open}
      onClose={() => setOpen(false)}
      title="Edit keywords"
      subtitle="
      Updated keywords will receive new volume while retaining everything else (e.g. tags)"
    >
      {keywordQuery.query.loading && (
        <VoxelDialogContent classMinWidth="min-w-[800px]" className="my-3">
          <CircularProgress />
        </VoxelDialogContent>
      )}
      <VoxelDialogContent classMinWidth="min-w-[800px]" className="mt-6">
        {!keywordQuery.query.loading && (
          <div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-row gap-3 items-end">
                {hasTranslations && (
                  <div className="basis-1/3">
                    <VoxelText variant="bread-medium" color="dark">
                      Translated from
                    </VoxelText>
                  </div>
                )}
                <div className="basis-1/3">
                  <VoxelText variant="bread-medium" color="dark">
                    Current keyword
                  </VoxelText>
                </div>
                <div className="grow">
                  <VoxelText variant="bread-medium" color="dark">
                    New keyword
                  </VoxelText>
                </div>
              </div>
              {keywordQuery.keywords.map((kw, index) => (
                <div className="flex flex-row gap-3 items-center">
                  {hasTranslations && <div className="basis-1/32">{kw.translatedFromKeyword}</div>}
                  <div className="basis-1/3">{kw.keyword}</div>
                  <div className="grow">
                    <VoxelInput
                      value={formBag.values[index]?.renamedKeyword ?? ''}
                      inputWidthClass="w-full"
                      onChange={(event) =>
                        formBag.setValues((prev) => {
                          const next = [...prev];
                          next[index] = { ...next[index], renamedKeyword: event.target.value };
                          return next;
                        })
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel
          onClick={() => setOpen(false)}
          disabled={formBag.isSubmitting || loading}
        />
        <VoxelDialogActionSubmit
          disabled={formBag.isSubmitting || loading}
          onClick={formBag.submitForm}
          title="Update keywords"
        />
      </VoxelDialogActions>
      {keywordQuery?.totalCount > 100 && (
        <VoxelAlert size="sm" iconSize="lg" iconPosition="center" severity="warning">
          {keywordQuery.totalCount} keywords selected - only 100 will show
        </VoxelAlert>
      )}
    </VoxelDialog>
  );
};

export default EditKeywordsDialog;
